import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { IclubAverageTotalWages, IleagueAverageTotalWages, IYAxisValue } from "../../../types/IClubDetail";
import LabelText from "../../../ui/LabelText";
import styled from "styled-components";
import { useTheme } from "../../../contexts/ThemeContext";
import LargeModal from "../../../ui/LargeModal";
import { useHorizontalScroll } from "../../../utils/HorizontalScrollHook";

interface LineBarChartProps {
  xAxisValues: string[];
  yAxisValues: IYAxisValue[];
  clubWages: IclubAverageTotalWages[];
  leagueWages: IleagueAverageTotalWages[];
}

const ChartContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

const LineBarChart: React.FC<LineBarChartProps> = ({ xAxisValues, yAxisValues, clubWages, leagueWages }) => {
  const { theme } = useTheme();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const ySvgRef = useRef<SVGSVGElement | null>(null);
  const scrollRef = useHorizontalScroll();
  const colors = [theme.playerCardBackground, theme.playerContentBackground];
  const [isLargeModalOpen, setLargeModalIsOpen] = useState(false);

  const toggleLargeModal = () => {
    setLargeModalIsOpen(!isLargeModalOpen);
  };

  const modalData = {
    headerTitle: "Data not provided by club",
    actionTag: " ",
    items: [
      {
        subTitle: " ",
        description:
          "Leeds United are situated in a country who’s laws to not require them to make public the data we’d need to fully calculate this sum.",
      },
    ],
  };

  useEffect(() => {
    if (!svgRef.current) return;
    if (xAxisValues === undefined || yAxisValues === undefined) return;
    const margin = { top: 100, right: 0, bottom: 100, left: 0 };
    const width = xAxisValues.length > 5 ? xAxisValues.length * 120 : xAxisValues.length * 175;
    const isTrue: boolean = Array.isArray(clubWages) && Array.isArray(leagueWages) ? true : false;
    const height = isTrue ? 800 : 400 - margin.top - margin.bottom;
    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .attr("overflow", "scroll");
    const yAxisSvg = d3
      .select(ySvgRef.current)
      .attr("width", 200)
      .attr("height", height + margin.top + margin.bottom);
    svg.selectAll("*").remove();
    yAxisSvg.selectAll("*").remove();
    const mainGroup = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    const yMainFGroup = yAxisSvg.append("g").attr("transform", `translate(${margin.left + 200},${margin.top})`);
    const backgroundGroup = mainGroup.append("g");
    const textGroup = mainGroup.append("g");
    const lineGroup = mainGroup.append("g");
    const yAxisGroup = yMainFGroup.append("g");
    const yAxisLineGroup = yMainFGroup.append("g");
    const x = d3.scaleBand<string>().domain(xAxisValues).range([0, width]).padding(0.1);
    const y = d3
      .scaleLinear()
      .domain([d3.min(yAxisValues, (d) => d.value)!, d3.max(yAxisValues, (d) => d.value)!])
      .range([height, 0]);
    const lineGenerator = d3
      .line<{ season: string; wages: number }>()
      .x((d) => x(d.season)! + x.bandwidth() / 2)
      .y((d) => y(d.wages));
    xAxisValues.forEach((d, i) => {
      backgroundGroup
        .append("rect")
        .attr("x", x(d)!)
        .attr("y", 0)
        .attr("width", x.bandwidth() + 20)
        .attr("height", height)
        .attr("stroke", colors[i % 2])
        .attr("fill", colors[i % 2]);
    });
    xAxisValues.forEach((d) => {
      textGroup
        .append("text")
        .attr("x", x(d)! + x.bandwidth() / 2) // Center the text below each x-tick
        .attr("y", height + 30) // Position below the x-axis
        .attr("text-anchor", "middle")
        .style("fill", theme.playerColor) // Set text color
        .style("font-size", "14px")
        .style("font-family", "DM Mono")
        .style("font-weight", 500)
        .text(d);
    });
    yAxisValues.forEach((d) => {
      yAxisGroup
        .append("text")
        .attr("x", -15)
        .attr("y", y(d.value) - 15)
        .attr("text-anchor", "end")
        .attr("dy", ".35em")
        .style("fill", theme.playerColor) // Set text color
        .style("font-size", "20px")
        .style("font-family", "DM Mono")
        .style("font-weight", 500)
        .text(d.text);
    });
    const yValues = yAxisValues.map((d) => d.value);
    for (let i = 0; i < yValues.length; i++) {
      lineGroup
        .append("line")
        .attr("class", "horizontal-line")
        .attr("x1", -180)
        .attr("x2", width)
        .attr("y1", y(yValues[i]))
        .attr("y2", y(yValues[i]))
        .attr("stroke", theme.playerDataBorder)
        .attr("stroke-width", 1);
    }
    for (let i = 0; i < yValues.length; i++) {
      yAxisLineGroup
        .append("line")
        .attr("x1", -180)
        .attr("class", "horizontal-yline")
        .attr("x2", width)
        .attr("y1", y(yValues[i]))
        .attr("y2", y(yValues[i]))
        .attr("stroke", theme.playerDataBorder)
        .attr("stroke-width", 1);
    }
    yAxisGroup
      .append("line")
      .attr("x1", -2)
      .attr("y1", 0 - margin.bottom)
      .attr("x2", -2)
      .attr("y2", height + margin.top + 50)
      .style("stroke", theme.playerDataBorder)
      .style("stroke-width", 3);
    yAxisGroup
      .append("text")
      .attr("x", -10) // Position to the left of the axis
      .attr("y", -60)
      .attr("text-anchor", "end")
      .attr("dy", ".35em")
      .style("fill", theme.playerColor) // Set text color
      .style("font-size", "14px")
      .style("font-family", "DM Mono")
      .style("font-weight", 500)
      .text("TOTAL € PER PERSON");
    yAxisGroup
      .append("text")
      .attr("x", -150) // Position to the left of the axis
      .attr("y", isTrue ? 830 : 230)
      .attr("dy", ".35em")
      .style("fill", theme.playerColor) // Set text color
      .style("font-size", "12px")
      .style("font-family", "DM Mono")
      .style("font-weight", 500)
      .text("AV. TOTAL WAGES");
    yAxisGroup
      .append("text")
      .attr("x", -150) // Position to the left of the axis
      .attr("y", isTrue ? 860 : 260)
      .attr("dy", ".35em")
      .style("fill", theme.playerColor)
      .style("font-size", "12px")
      .style("font-family", "DM Mono")
      .style("font-weight", 500)
      .text("LEAGUE WAGES");
    yAxisGroup
      .append("circle")
      .attr("r", 6)
      .attr("cx", -170)
      .attr("cy", isTrue ? 860 : 260)
      .style("fill", "#FF7800");
    yAxisGroup
      .append("circle")
      .attr("r", 6)
      .attr("cx", -170)
      .attr("cy", isTrue ? 830 : 230)
      .style("fill", "#00D1A8");
    if (isTrue) {
      lineGroup
        .append("path")
        .datum(clubWages)
        .style("stroke", "#FF7800")
        .style("fill", "none")
        .style("stroke-width", 2)
        .attr("d", lineGenerator);
      lineGroup
        .append("path")
        .datum(leagueWages)
        .style("stroke", "#00D1A8")
        .style("fill", "none")
        .style("stroke-width", 4)
        .attr("d", lineGenerator);
      const tooltip = d3
        .select("body")
        .append("div")
        .style("position", "fixed")
        .style("z-index", 999)
        .style("visibility", "hidden")
        .style("background-color", theme.secondaryButtonBackground)
        .style("color", theme.color)
        .style("border", "1px solid ${theme.secondaryButtonBackground}")
        .style("padding", "8px")
        .style("border-radius", "4px")
        .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)");
      lineGroup
        .selectAll(".club-dot")
        .data(clubWages)
        .enter()
        .append("circle")
        .attr("class", "league-dot")
        .attr("r", 10)
        .attr("cx", (d) => x(d.season)! + x.bandwidth() / 2)
        .attr("cy", (d) => y(d.wages))
        .style("fill", "white")
        .style("stroke", "black")
        .style("cursor", "pointer")
        .style("opacity", 0)
        .on("mouseover", (event, d: any) => {
          d3.selectAll(".club-dot").style("opacity", 1);
          tooltip
            .style("visibility", "visible")
            .html(`League: €${d.wagesText}`)
            .style("left", `${event.clientX - 50}px`)
            .style("top", `${event.clientY - 50}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
          d3.selectAll(".club-dot").style("opacity", 0);
        });
      textGroup
        .selectAll(".league-dot")
        .data(leagueWages)
        .enter()
        .append("circle")
        .attr("class", "league-dot")
        .attr("r", 10)
        .attr("cx", (d) => x(d.season)! + x.bandwidth() / 2)
        .attr("cy", (d) => y(d.wages))
        .style("fill", "white")
        .style("stroke", "black")
        .style("cursor", "pointer")
        .style("opacity", 0)
        .on("mouseover", (event, d: any) => {
          d3.selectAll(".club-dot").style("opacity", 1);
          tooltip
            .style("visibility", "visible")
            .html(`Total: €${d.wagesText}`)
            .style("left", `${event.clientX - 50}px`)
            .style("top", `${event.clientY - 50}px`);
        })
        .on("mousemove", (event) => {
          tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
          d3.selectAll(".club-dot").style("opacity", 0);
        });
    } else {
      backgroundGroup
        .append("rect")
        .attr("x", width / 2 - 85)
        .attr("y", height / 2 + -40)
        .attr("text-anchor", "middle")
        .attr("width", 190)
        .attr("height", 35)
        .style("stroke", theme.emptyDataBorder)
        .style("cursor", "pointer")
        .style("fill", theme.playerDataBorder)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen);
        });
      const displayText = "Data not provided by club";
      textGroup
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2 + -20)
        .attr("text-anchor", "middle") // Center the text
        .style("font-family", "Aeonik") // Set font family to Aeonik
        .style("font-size", "12px") // Set font size
        .style("font-weight", 500)
        .style("cursor", "pointer")
        .style("fill", theme.playerColor) // Set text color
        .text(displayText)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
        });
      const iconX = width / 2 + 75; // Adjust the X position as needed
      const iconY = height / 2 + -32; // Adjust the Y position as needed
      lineGroup
        .append("path")
        .attr(
          "d",
          "M 11 18 h 2 v -2 h -2 Z m 1 -16 C 6.48 2 2 6.48 2 12 s 4.48 10 10 10 s 10 -4.48 10 -10 S 17.52 2 12 2 m 0 18 c -4.41 0 -8 -3.59 -8 -8 s 3.59 -8 8 -8 s 8 3.59 8 8 s -3.59 8 -8 8 m 0 -14 c -2.21 0 -4 1.79 -4 4 h 2 c 0 -1.1 0.9 -2 2 -2 s 2 0.9 2 2 c 0 2 -3 1.75 -3 5 h 2 c 0 -2.25 3 -2.5 3 -5 c 0 -2.21 -1.79 -4 -4 -4"
        )
        .attr("transform", `translate(${iconX}, ${iconY})`)
        .attr("transform", `translate(${iconX}, ${iconY}) scale(0.7)`) // Scale down by 50%
        .style("fill", theme.playerColor)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
        });
    }
  }, [xAxisValues, yAxisValues, clubWages, leagueWages, theme]);

  return (
    <>
      <LargeModal toggleLargeModal={toggleLargeModal} isOpen={isLargeModalOpen} data={modalData} />
      <div className="header-content-wrapper">
        <LabelText className="player-search-text linebar-chart-text">Wages over time</LabelText>
        <ChartContainer style={{ display: "flex", width: "100%" }}>
          <div style={{ filter: "drop-shadow(0 0 20rem black)" }}>
            <svg ref={ySvgRef}></svg>
          </div>
          <div
            ref={scrollRef}
            style={{
              overflowX: "auto",
              position: "relative",
              overflow: "auto",
              width: "100%",
            }}
          >
            <svg id="chart" ref={svgRef}></svg>
          </div>
        </ChartContainer>
      </div>
    </>
  );
};

export default LineBarChart;
